// src/pages/PatientPortal.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import nooLogo from '../assets/noo.png';
import leftArrowIcon from '../assets/left.png';
import manImage from '../assets/man.jpg';
import backImage from '../assets/back.jpg';

const displayFont = { fontFamily: "'Sora', ui-sans-serif, system-ui, sans-serif" };
const bodyFont = { fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif" };
const monoFont = { fontFamily: "'IBM Plex Mono', ui-monospace, SFMono-Regular, monospace" };

const statusStyles = {
  Scheduled: 'bg-[#4FB3A9]/15 text-[#0F3D3E]',
  Completed: 'bg-[#0F3D3E] text-white',
  Cancelled: 'bg-red-100 text-red-700',
};

const statusDot = {
  Scheduled: 'bg-[#4FB3A9]',
  Completed: 'bg-[#0F3D3E]',
  Cancelled: 'bg-red-500',
};

const InfoRow = ({ label, value }) => (
  <p className="text-sm">
    <span className="text-[#0F3D3E]/60 font-semibold" style={monoFont}>{label}: </span>
    <span className="text-[#22262B]">{value ?? 'N/A'}</span>
  </p>
);

const PatientPortal = () => {
  const mockAllPatientsData = [
    {
      id: 'pat001',
      name: 'Nour Ali',
      bloodType: '+A',
      gender: 'Male',
      dateOfBirth: '1990-07-23',
      telephone: '01111112233',
      maritalStatus: 'Single',
      occupation: 'Front end developer',
      email: 'Nour.Ali@example.com',
      address: 'Cairo, 23 Alsalim Street',
      visits: [
        {
          visitDate: '2024-07-28',
          vitals: { height: 190, weight: 90, bmi: 24.9, arterialBloodOxygen: 110, respiratoryRate: 16, bloodPressure: '120/80', pulse: 80, temperature: 37 },
          diagnosis: { diseaseName: 'Gastritis', classification: 'Acute', organsHarmed: 'Stomach', lastObservation: '2024-07-28', timesOfInjury: 1, timeStamp: '2024-07-28 10:00 AM', clinicalNote: 'Patient reports severe abdominal pain.' },
          conditions: { conditions: 'N/A', onSetDate: 'N/A', activation: 'N/A', action: 'N/A', endDate: 'N/A' },
          medicationOther: { drug: 'Omeprazole', food: 'Spicy food (avoid)', other: 'N/A', comment: 'Initial prescription.', reaction: 'N/A', severity: 'Moderate' },
          attachments: [{ id: 1, src: backImage, comment: 'Stomach Ultrasound' }],
          modelResults: { classificationImage: backImage, classificationComment: 'Normal-cecum', segmentationImage: backImage, segmentationComment: 'N/A' },
          soapNotes: { subjective: "Patient reports persistent abdominal pain, nausea.", objective: "Abdominal tenderness.", assessment: "Acute gastritis.", plan: "Prescribe Omeprazole, dietary advice." }
        },
        {
          visitDate: '2023-11-15',
          vitals: { height: 188, weight: 88, bmi: 24.8, arterialBloodOxygen: 108, respiratoryRate: 18, bloodPressure: '118/78', pulse: 78, temperature: 36.8 },
          diagnosis: { diseaseName: 'GERD', classification: 'Chronic', organsHarmed: 'Esophagus', lastObservation: '2023-11-15', timesOfInjury: 1, timeStamp: '2023-11-15 10:00 AM', clinicalNote: 'Patient presented with heartburn and indigestion.' },
          conditions: { conditions: 'N/A', onSetDate: 'N/A', activation: 'N/A', action: 'N/A', endDate: 'N/A' },
          medicationOther: { drug: 'Antacids', food: 'Spicy food (avoid)', other: 'N/A', comment: 'Advised lifestyle modifications.', reaction: 'N/A', severity: 'Mild' },
          attachments: [],
          modelResults: { classificationImage: backImage, classificationComment: 'No significant findings', segmentationImage: backImage, segmentationComment: 'N/A' },
          soapNotes: { subjective: "Patient reports recurring heartburn.", objective: "No significant physical findings.", assessment: "GERD.", plan: "Dietary changes, OTC antacids." }
        },
      ],
    },
    {
      id: 'pat002',
      name: 'Fatma Khaled',
      bloodType: 'O+',
      gender: 'Female',
      dateOfBirth: '1985-03-10',
      telephone: '01000001122',
      maritalStatus: 'Married',
      occupation: 'Teacher',
      email: 'fatma.khaled@example.com',
      address: 'Giza, 10 Pyramids St.',
      visits: [
        {
          visitDate: '2024-07-20',
          vitals: { height: 165, weight: 65, bmi: 23.9, arterialBloodOxygen: 98, respiratoryRate: 18, bloodPressure: '110/70', pulse: 75, temperature: 36.9 },
          diagnosis: { diseaseName: 'IBS', classification: 'Chronic', organsHarmed: 'Intestines', lastObservation: '2024-07-20', timesOfInjury: 5, timeStamp: '2024-07-20 09:00 AM', clinicalNote: 'Patient reports alternating constipation and diarrhea.' },
          conditions: { conditions: 'Stress', onSetDate: '2020-01-01', activation: 'Active', action: 'Stress management', endDate: 'N/A' },
          medicationOther: { drug: 'Loperamide', food: 'High FODMAP foods (avoid)', other: 'Probiotics', comment: 'Symptoms managed with diet and medication.', reaction: 'N/A', severity: 'Moderate' },
          attachments: [],
          modelResults: { classificationImage: backImage, classificationComment: 'Normal', segmentationImage: backImage, segmentationComment: 'N/A' },
          soapNotes: { subjective: "Abdominal discomfort, bloating, altered bowel habits.", objective: "Mild abdominal distension.", assessment: "Irritable Bowel Syndrome (IBS).", plan: "Low FODMAP diet, Loperamide PRN, stress reduction." }
        },
      ],
    },
  ];

  const mockAllAppointments = [
    { id: 'app1', patientId: 'pat001', patientName: 'Nour Ali', doctorId: 'doc1', doctorName: 'Dr. Ahmed Hassan', date: '2024-08-01', time: '10:00', notes: 'Follow-up on gastritis.', status: 'Scheduled' },
    { id: 'app2', patientId: 'pat002', patientName: 'Fatma Khaled', doctorId: 'doc2', doctorName: 'Dr. Sara Ali', date: '2024-08-01', time: '11:30', notes: 'New patient consultation.', status: 'Scheduled' },
    { id: 'app3', patientId: 'pat001', patientName: 'Nour Ali', doctorId: 'doc1', doctorName: 'Dr. Ahmed Hassan', date: '2024-08-02', time: '09:00', notes: 'Endoscopy procedure.', status: 'Scheduled' },
    { id: 'app4', patientId: 'pat001', patientName: 'Nour Ali', doctorId: 'doc1', doctorName: 'Dr. Ahmed Hassan', date: '2024-07-28', time: '10:00', notes: 'Initial visit for abdominal pain.', status: 'Completed' },
    { id: 'app5', patientId: 'pat002', patientName: 'Fatma Khaled', doctorId: 'doc2', doctorName: 'Dr. Sara Ali', date: '2024-07-20', time: '09:00', notes: 'IBS consultation.', status: 'Completed' },
  ];

  const [loggedInPatientId, setLoggedInPatientId] = useState('');
  const [inputPatientId, setInputPatientId] = useState('');
  const [loginError, setLoginError] = useState('');

  const [patientData, setPatientData] = useState(null);
  const [patientAppointments, setPatientAppointments] = useState([]);

  const [activeSection, setActiveSection] = useState('profile');
  const [selectedVisitIndex, setSelectedVisitIndex] = useState(0);

  const handleLogin = (e) => {
    e.preventDefault();
    const foundPatient = mockAllPatientsData.find(p => p.id === inputPatientId);
    if (foundPatient) {
      setLoggedInPatientId(inputPatientId);
      setPatientData(foundPatient);
      setPatientAppointments(mockAllAppointments.filter(app => app.patientId === inputPatientId));
      setLoginError('');
      setSelectedVisitIndex(foundPatient.visits.length > 0 ? 0 : -1);
    } else {
      setLoginError('Invalid Patient ID. Please try again.');
      setLoggedInPatientId('');
      setPatientData(null);
      setPatientAppointments([]);
      setSelectedVisitIndex(-1);
    }
  };

  const selectedVisitData = patientData?.visits[selectedVisitIndex] || {};

  const sectionLabels = {
    profile: 'My Profile',
    appointments: 'My Appointments',
    'medical-records': 'My Medical Record',
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]" style={bodyFont}>
      {/* Header */}
      <div className="bg-white border-b border-[#E4E1D8]">
        <div className="container mx-auto px-4 md:px-8 py-4 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="/home">
              <img src={nooLogo} alt="Drogastro" className="w-12 h-12 object-contain" />
            </Link>
            <div>
              <p className="text-[11px] tracking-wider text-[#0F3D3E]/50" style={monoFont}>PATIENT ACCESS</p>
              <h1 className="text-xl md:text-2xl font-bold text-[#0F3D3E]" style={displayFont}>Patient Portal</h1>
            </div>
          </div>
          <Link
            to="/home"
            className="flex items-center gap-2 bg-[#FAF9F6] hover:bg-[#E4E1D8] text-[#0F3D3E] font-semibold py-2 px-4 rounded-md border border-[#E4E1D8] transition-colors duration-200 text-sm"
          >
            <img src={leftArrowIcon} alt="Back" className="w-4 h-4" /> Back to Home
          </Link>
        </div>
      </div>

      {!loggedInPatientId ? (
        // Login Form
        <div className="flex justify-center items-center min-h-[calc(100vh-90px)] px-4">
          <div className="bg-white p-8 rounded-xl border border-[#E4E1D8] shadow-sm w-full max-w-md">
            <p className="text-[11px] tracking-wider text-[#D6A419] mb-2" style={monoFont}>SIGN IN</p>
            <h2 className="text-xl font-bold text-[#0F3D3E] mb-6" style={displayFont}>Enter Your Patient ID</h2>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label htmlFor="patientIdInput" className="sr-only">Patient ID</label>
                <input
                  type="text"
                  id="patientIdInput"
                  value={inputPatientId}
                  onChange={(e) => setInputPatientId(e.target.value)}
                  placeholder="e.g., pat001"
                  className="w-full p-3 border border-[#E4E1D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#4FB3A9] transition-shadow text-base"
                  required
                />
              </div>
              {loginError && <p className="text-red-600 text-sm">{loginError}</p>}
              <button
                type="submit"
                className="w-full bg-[#0F3D3E] hover:bg-[#0F3D3E]/90 text-white font-semibold py-3 px-6 rounded-md shadow-sm transition-colors duration-200 text-base"
              >
                Login
              </button>
            </form>
          </div>
        </div>
      ) : (
        <div className="container mx-auto px-4 md:px-8 py-6 md:py-8">
          <div className="bg-white rounded-xl border border-[#E4E1D8] shadow-sm p-5 md:p-8">
            {/* Section Navigation */}
            <div className="flex flex-wrap gap-1 border-b border-[#E4E1D8] mb-6 -mx-1">
              {['profile', 'appointments', 'medical-records'].map((section) => (
                <button
                  key={section}
                  className={`px-5 py-3 text-sm font-semibold border-b-2 transition-colors duration-200 ${
                    activeSection === section
                      ? 'border-[#0F3D3E] text-[#0F3D3E]'
                      : 'border-transparent text-[#22262B]/50 hover:text-[#0F3D3E]'
                  }`}
                  onClick={() => setActiveSection(section)}
                >
                  {sectionLabels[section]}
                </button>
              ))}
            </div>

            {/* Profile */}
            {activeSection === 'profile' && patientData && (
              <div>
                <h2 className="text-lg font-bold text-[#0F3D3E] mb-5" style={displayFont}>My Profile</h2>
                <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
                  <img src={manImage} alt="Patient portrait" className="w-28 h-28 object-cover rounded-full border-2 border-[#4FB3A9] flex-shrink-0" />
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3 w-full">
                    <InfoRow label="Name" value={patientData.name} />
                    <InfoRow label="Blood Type" value={patientData.bloodType} />
                    <InfoRow label="ID" value={patientData.id} />
                    <InfoRow label="Gender" value={patientData.gender} />
                    <InfoRow label="Date of Birth" value={patientData.dateOfBirth} />
                    <InfoRow label="Telephone" value={patientData.telephone} />
                    <InfoRow label="Marital Status" value={patientData.maritalStatus} />
                    <InfoRow label="Occupation" value={patientData.occupation} />
                    <InfoRow label="Email" value={patientData.email} />
                    <InfoRow label="Address" value={patientData.address} />
                  </div>
                </div>
              </div>
            )}

            {/* Appointments */}
            {activeSection === 'appointments' && (
              <div>
                <div className="flex items-center justify-between mb-5">
                  <h2 className="text-lg font-bold text-[#0F3D3E]" style={displayFont}>My Appointments</h2>
                  <span className="text-xs font-semibold text-[#0F3D3E]/50 bg-[#FAF9F6] border border-[#E4E1D8] rounded-full px-3 py-1" style={monoFont}>
                    {patientAppointments.length} TOTAL
                  </span>
                </div>
                {patientAppointments.length === 0 ? (
                  <div className="flex flex-col items-center justify-center text-center py-14 px-6 bg-[#FAF9F6] rounded-lg border border-dashed border-[#E4E1D8]">
                    <p className="text-[#22262B]/60 text-base">You have no appointments scheduled.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto rounded-md border border-[#E4E1D8]">
                    <table className="min-w-full bg-white">
                      <thead className="bg-[#0F3D3E]/5">
                        <tr>
                          <th className="py-3 px-4 text-left text-xs font-semibold text-[#0F3D3E] uppercase tracking-wider" style={monoFont}>Date</th>
                          <th className="py-3 px-4 text-left text-xs font-semibold text-[#0F3D3E] uppercase tracking-wider" style={monoFont}>Time</th>
                          <th className="py-3 px-4 text-left text-xs font-semibold text-[#0F3D3E] uppercase tracking-wider" style={monoFont}>Doctor</th>
                          <th className="py-3 px-4 text-left text-xs font-semibold text-[#0F3D3E] uppercase tracking-wider" style={monoFont}>Notes</th>
                          <th className="py-3 px-4 text-left text-xs font-semibold text-[#0F3D3E] uppercase tracking-wider" style={monoFont}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {patientAppointments.map(app => (
                          <tr key={app.id} className="border-t border-[#E4E1D8] hover:bg-[#FAF9F6] transition-colors">
                            <td className="py-3 px-4 whitespace-nowrap text-[#22262B]">{app.date}</td>
                            <td className="py-3 px-4 whitespace-nowrap text-[#22262B] font-semibold" style={monoFont}>{app.time}</td>
                            <td className="py-3 px-4 whitespace-nowrap text-[#22262B]">{app.doctorName}</td>
                            <td className="py-3 px-4 text-[#22262B]/80">{app.notes}</td>
                            <td className="py-3 px-4 whitespace-nowrap">
                              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${statusStyles[app.status] || 'bg-gray-200 text-gray-800'}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${statusDot[app.status] || 'bg-gray-500'}`} />
                                {app.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Medical Records */}
            {activeSection === 'medical-records' && patientData && (
              <div>
                <h2 className="text-lg font-bold text-[#0F3D3E] mb-5" style={displayFont}>My Medical Record</h2>

                {patientData.visits.length === 0 ? (
                  <div className="flex flex-col items-center justify-center text-center py-14 px-6 bg-[#FAF9F6] rounded-lg border border-dashed border-[#E4E1D8]">
                    <p className="text-[#22262B]/60 text-base">No medical record found for you.</p>
                  </div>
                ) : (
                  <>
                    <div className="mb-6">
                      <label htmlFor="visit-selector" className="block text-xs font-semibold text-[#0F3D3E]/70 mb-1.5" style={monoFont}>SELECT VISIT DATE</label>
                      <select
                        id="visit-selector"
                        className="w-full sm:w-64 p-2.5 border border-[#E4E1D8] rounded-md bg-white text-[#22262B] focus:outline-none focus:ring-2 focus:ring-[#4FB3A9] transition-shadow"
                        value={selectedVisitIndex}
                        onChange={(e) => setSelectedVisitIndex(parseInt(e.target.value))}
                      >
                        {patientData.visits.map((visit, index) => (
                          <option key={index} value={index}>{visit.visitDate}</option>
                        ))}
                      </select>
                    </div>

                    {selectedVisitIndex !== -1 && (
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
                        <div className="p-5 bg-[#FAF9F6] rounded-lg border border-[#E4E1D8]">
                          <h4 className="text-sm font-bold mb-3 text-[#0F3D3E] tracking-wide" style={monoFont}>VITAL SIGNS</h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-4">
                            <InfoRow label="Height (cm)" value={selectedVisitData.vitals?.height} />
                            <InfoRow label="Weight (kg)" value={selectedVisitData.vitals?.weight} />
                            <InfoRow label="BMI" value={selectedVisitData.vitals?.bmi} />
                            <InfoRow label="Blood Oxygen" value={selectedVisitData.vitals?.arterialBloodOxygen} />
                            <InfoRow label="Respiratory Rate" value={selectedVisitData.vitals?.respiratoryRate} />
                            <InfoRow label="Blood Pressure" value={selectedVisitData.vitals?.bloodPressure} />
                            <InfoRow label="Pulse" value={selectedVisitData.vitals?.pulse} />
                            <InfoRow label="Temperature (°C)" value={selectedVisitData.vitals?.temperature} />
                          </div>
                        </div>

                        <div className="p-5 bg-[#FAF9F6] rounded-lg border border-[#E4E1D8]">
                          <h4 className="text-sm font-bold mb-3 text-[#0F3D3E] tracking-wide" style={monoFont}>DIAGNOSIS</h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-4">
                            <InfoRow label="Disease" value={selectedVisitData.diagnosis?.diseaseName} />
                            <InfoRow label="Classification" value={selectedVisitData.diagnosis?.classification} />
                            <InfoRow label="Organs Harmed" value={selectedVisitData.diagnosis?.organsHarmed} />
                            <InfoRow label="Last Observation" value={selectedVisitData.diagnosis?.lastObservation} />
                            <InfoRow label="Times of Injury" value={selectedVisitData.diagnosis?.timesOfInjury} />
                            <InfoRow label="Timestamp" value={selectedVisitData.diagnosis?.timeStamp} />
                            <p className="col-span-full text-sm">
                              <span className="text-[#0F3D3E]/60 font-semibold" style={monoFont}>Clinical Note: </span>
                              <span className="text-[#22262B]">{selectedVisitData.diagnosis?.clinicalNote || 'N/A'}</span>
                            </p>
                          </div>
                        </div>

                        <div className="p-5 bg-[#FAF9F6] rounded-lg border border-[#E4E1D8]">
                          <h4 className="text-sm font-bold mb-3 text-[#0F3D3E] tracking-wide" style={monoFont}>CONDITIONS</h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-4">
                            <InfoRow label="Conditions" value={selectedVisitData.conditions?.conditions} />
                            <InfoRow label="Onset Date" value={selectedVisitData.conditions?.onSetDate} />
                            <InfoRow label="Activation" value={selectedVisitData.conditions?.activation} />
                            <InfoRow label="Action" value={selectedVisitData.conditions?.action} />
                            <InfoRow label="End Date" value={selectedVisitData.conditions?.endDate} />
                          </div>
                        </div>

                        <div className="p-5 bg-[#FAF9F6] rounded-lg border border-[#E4E1D8]">
                          <h4 className="text-sm font-bold mb-3 text-[#0F3D3E] tracking-wide" style={monoFont}>ALLERGY / MEDICATION</h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-4">
                            <InfoRow label="Drug" value={selectedVisitData.medicationOther?.drug} />
                            <InfoRow label="Food" value={selectedVisitData.medicationOther?.food} />
                            <InfoRow label="Other" value={selectedVisitData.medicationOther?.other} />
                            <InfoRow label="Comment" value={selectedVisitData.medicationOther?.comment} />
                            <InfoRow label="Reaction" value={selectedVisitData.medicationOther?.reaction} />
                            <InfoRow label="Severity" value={selectedVisitData.medicationOther?.severity} />
                          </div>
                        </div>

                        <div className="p-5 bg-[#FAF9F6] rounded-lg border border-[#E4E1D8] lg:col-span-2">
                          <h4 className="text-sm font-bold mb-3 text-[#0F3D3E] tracking-wide" style={monoFont}>SOAP NOTES</h4>
                          <div className="space-y-3">
                            <InfoRow label="Subjective" value={selectedVisitData.soapNotes?.subjective} />
                            <InfoRow label="Objective" value={selectedVisitData.soapNotes?.objective} />
                            <InfoRow label="Assessment" value={selectedVisitData.soapNotes?.assessment} />
                            <InfoRow label="Plan" value={selectedVisitData.soapNotes?.plan} />
                          </div>
                        </div>

                        <div className="p-5 bg-[#FAF9F6] rounded-lg border border-[#E4E1D8] lg:col-span-2">
                          <h4 className="text-sm font-bold mb-4 text-[#0F3D3E] tracking-wide" style={monoFont}>ATTACHMENTS</h4>
                          {selectedVisitData.attachments?.length > 0 ? (
                            <div className="flex flex-wrap gap-4">
                              {selectedVisitData.attachments.map((attachment, index) => (
                                <div key={index} className="flex flex-col items-center">
                                  <img src={attachment.src} alt={`Attachment ${index + 1}`} className="w-56 h-36 object-cover rounded-md border border-[#E4E1D8] shadow-sm" />
                                  <p className="text-xs mt-2 text-center text-[#22262B]/70">{attachment.comment}</p>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-[#22262B]/50">No attachments found for this visit.</p>
                          )}
                        </div>

                        <div className="p-5 bg-[#FAF9F6] rounded-lg border border-[#E4E1D8] lg:col-span-2">
                          <h4 className="text-sm font-bold mb-4 text-[#0F3D3E] tracking-wide" style={monoFont}>MODEL RESULTS</h4>
                          <div className="flex flex-wrap gap-4">
                            <div className="flex flex-col items-center">
                              <img src={selectedVisitData.modelResults?.classificationImage || backImage} alt="Classification result" className="w-56 h-36 object-cover rounded-md border border-[#E4E1D8] shadow-sm" />
                              <p className="text-xs mt-2 text-center text-[#22262B]/70">Classification: {selectedVisitData.modelResults?.classificationComment || 'N/A'}</p>
                            </div>
                            <div className="flex flex-col items-center">
                              <img src={selectedVisitData.modelResults?.segmentationImage || backImage} alt="Segmentation result" className="w-56 h-36 object-cover rounded-md border border-[#E4E1D8] shadow-sm" />
                              <p className="text-xs mt-2 text-center text-[#22262B]/70">Segmentation: {selectedVisitData.modelResults?.segmentationComment || 'N/A'}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientPortal;
